/**
 * Shippo SDK response contracts — parsed envelope at the adapter boundary
 * (`lib/shippo/client.ts`).
 *
 * Sibling-F · F9. The Shippo SDK returns its own loose TS types
 * (`Awaited<ReturnType<typeof shippo.shipments.create>>` etc.); without a
 * Zod safeParse on the seam, every downstream consumer trusts those types
 * blindly. A schema drift in Shippo's wire shape (field renamed, currency
 * widened, `rates` array missing) propagates silently to label-buy and to
 * the money-fold rolldown.
 *
 * These schemas pin EXACTLY the camelCase surface our adapter consumes —
 * not Shippo's full API. The SDK normalises Shippo's snake_case wire to
 * camelCase before we see the response; that camelCase shape is what we
 * pin here. After a successful `safeParse` the producer-pin is enforced;
 * the rest of the adapter (and every downstream consumer) trusts the
 * parsed shape (Parse, Don't Validate).
 *
 * **Why required-or-undefined, not nullable:** the SDK's TypeScript types
 * declare these optional fields as `T | undefined`. Real Shippo JSON
 * responses occasionally come back with `null` (e.g. `durationTerms: null`
 * in our `shippo-client-customs.test.ts` fixtures), so the optional fields
 * use `.nullish()` to accept both — preserving existing adapter behaviour
 * (`rate.durationTerms ?? null`).
 *
 * @see lib/shippo/client.ts — the only callers of these schemas (boundary)
 * @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-F · F9
 * @see .tondocs/shippo/HANDOFF_RELIABILITY_GAPS_2026_06_27.md PR-F
 * @see .tondocs/skills/type-driven-design.md §5 — Parse, Don't Validate
 */

import { z } from "zod";
import { pinned } from "./pin";
import { ISO_4217_CODES, type Iso4217 } from "./money";

// ---------------------------------------------------------------------------
// Rate — one element inside a Shipment's `rates[]` array
// ---------------------------------------------------------------------------

/**
 * Parsed Shippo Rate as the adapter consumes it. Only fields the adapter
 * actually reads downstream (`lib/shippo/client.ts::getShipmentRates` +
 * `::getShipment`) — additive Shippo fields (`amountLocal`, `objectCreated`,
 * `objectOwner`, `providerImage75`, …) are stripped by Zod's default
 * object-strip semantics so they cannot drift past this seam.
 *
 * `currency` is locked to {@link Iso4217} — a Shippo response carrying a
 * currency outside our 33-code set fails the parse at the boundary instead
 * of silently being defaulted to `"USD"` by the old `rate.currency ?? "USD"`
 * fallback. Surfacing the drift early is the point.
 */
export interface ShippoRateResponse {
  /** Shippo's rate `object_id`. Round-trips back to Shippo at label buy. */
  objectId: string;
  /** Major-unit price, stringified per Shippo wire (e.g. `"30.50"`). */
  amount: string;
  /** ISO 4217 — locked to the marketplace's Iso4217 surface. */
  currency: Iso4217;
  /** Carrier name (e.g. `"USPS"`, `"FedEx"`). */
  provider: string;
  /** Service tier — adapter reads `name` then falls back to `token`. */
  servicelevel: {
    token?: string | null;
    name?: string | null;
  };
  estimatedDays?: number | null;
  durationTerms?: string | null;
  attributes?: string[] | null;
  messages?: Array<{
    code?: string | null;
    source?: string | null;
    text?: string | null;
  }> | null;
}

/**
 * Outer envelope returned by `shippo.shipments.create` and
 * `shippo.shipments.get` — the parts of a Shippo Shipment the adapter
 * reads. Additional Shippo fields (parcels metadata, addresses, extras)
 * are not pinned here because the rates-only `getShipmentRates` does not
 * read them; `getShipment` reads them via a separate schema below.
 */
export interface ShippoRateResponseEnvelope {
  objectId?: string | null;
  rates?: ShippoRateResponse[] | null;
}

// ---------------------------------------------------------------------------
// Zod — pinned schemas
// ---------------------------------------------------------------------------

const NUMERIC_STRING = /^-?\d+(?:\.\d+)?$/;

export const ShippoRateResponseSchema = pinned<ShippoRateResponse>()(
  z.object({
    objectId: z.string().min(1),
    amount: z
      .string()
      .regex(NUMERIC_STRING, "amount must be a numeric string"),
    currency: z.enum(ISO_4217_CODES),
    provider: z.string().min(1),
    servicelevel: z.object({
      token: z.string().nullish(),
      name: z.string().nullish(),
    }),
    estimatedDays: z.number().nullish(),
    durationTerms: z.string().nullish(),
    attributes: z.array(z.string()).nullish(),
    messages: z
      .array(
        z.object({
          code: z.string().nullish(),
          source: z.string().nullish(),
          text: z.string().nullish(),
        }),
      )
      .nullish(),
  }),
);

export const ShippoRateResponseEnvelopeSchema =
  pinned<ShippoRateResponseEnvelope>()(
    z.object({
      objectId: z.string().nullish(),
      rates: z.array(ShippoRateResponseSchema).nullish(),
    }),
  );

// ---------------------------------------------------------------------------
// Address validation — `shippo.addresses.create({ validate: true })`
// ---------------------------------------------------------------------------

/**
 * Parsed Shippo address-validation envelope as consumed by
 * `lib/shippo/client.ts::validateAddress`. Only the fields the adapter
 * actually reads:
 *
 *  - `isComplete` — Shippo's address-parser verdict (populated for all
 *    countries; `false` = Shippo could not parse the address at all).
 *  - `validationResults.isValid` — USPS-backed validity (US-only signal;
 *    absent / undefined / null for non-US per
 *    `.tondocs/shippo/PROBES_2026_06_25.md §A`).
 *  - `validationResults.messages[].text` — operator-facing rejection
 *    reasons surfaced to the buyer when `isValid === false`.
 */
export interface ShippoAddressValidationMessage {
  text?: string | null;
  code?: string | null;
  source?: string | null;
  type?: string | null;
}

export interface ShippoAddressValidationResults {
  isValid?: boolean | null;
  messages?: ShippoAddressValidationMessage[] | null;
}

/**
 * WS4 (2026-07-23): `shippo.addresses.create({ validate: true })` echoes the
 * SDK's own `Address` object back alongside `validationResults` — the SAME
 * camelCase fields the `shipments.get` envelope carries on `addressFrom` /
 * `addressTo` (see `ShippoShipmentAddress` below). Adding them here lets
 * `validateAddress` surface a `normalizedAddress` (carrier-cleaned form) for
 * `seller_orders.shipping_address_normalized` — previously the adapter
 * discarded everything except `isComplete` / `validationResults`.
 */
export interface ShippoAddressValidationResponse {
  isComplete?: boolean | null;
  validationResults?: ShippoAddressValidationResults | null;
  name?: string | null;
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
}

export const ShippoAddressValidationMessageSchema =
  pinned<ShippoAddressValidationMessage>()(
    z.object({
      text: z.string().nullish(),
      code: z.string().nullish(),
      source: z.string().nullish(),
      type: z.string().nullish(),
    }),
  );

export const ShippoAddressValidationResultsSchema =
  pinned<ShippoAddressValidationResults>()(
    z.object({
      isValid: z.boolean().nullish(),
      messages: z.array(ShippoAddressValidationMessageSchema).nullish(),
    }),
  );

export const ShippoAddressValidationResponseSchema =
  pinned<ShippoAddressValidationResponse>()(
    z.object({
      isComplete: z.boolean().nullish(),
      validationResults: ShippoAddressValidationResultsSchema.nullish(),
      name: z.string().nullish(),
      street1: z.string().nullish(),
      street2: z.string().nullish(),
      city: z.string().nullish(),
      state: z.string().nullish(),
      zip: z.string().nullish(),
      country: z.string().nullish(),
      phone: z.string().nullish(),
      email: z.string().nullish(),
    }),
  );

// ---------------------------------------------------------------------------
// Shipment-get envelope — `shippo.shipments.get(id)` (Ready-to-ship re-rate)
// ---------------------------------------------------------------------------

/**
 * Parsed envelope for `shippo.shipments.get` as the adapter consumes it
 * inside `getShipment` (PLAN §L.1 step 1, Ready-to-ship re-rate path).
 * Carries the persisted parcel + addresses on top of the rate envelope so
 * `getShipment` can rebuild a `ShippoAddressInput` round-trip without
 * touching the raw SDK type.
 */
export interface ShippoShipmentAddress {
  name?: string | null;
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface ShippoShipmentParcel {
  weight?: string | null;
  massUnit?: string | null;
  length?: string | null;
  width?: string | null;
  height?: string | null;
  distanceUnit?: string | null;
}

export interface ShippoShipmentGetResponse {
  objectId?: string | null;
  addressFrom?: ShippoShipmentAddress | null;
  addressTo?: ShippoShipmentAddress | null;
  parcels?: ShippoShipmentParcel[] | null;
  rates?: ShippoRateResponse[] | null;
}

export const ShippoShipmentAddressSchema = pinned<ShippoShipmentAddress>()(
  z.object({
    name: z.string().nullish(),
    street1: z.string().nullish(),
    street2: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    zip: z.string().nullish(),
    country: z.string().nullish(),
    phone: z.string().nullish(),
    email: z.string().nullish(),
  }),
);

export const ShippoShipmentParcelSchema = pinned<ShippoShipmentParcel>()(
  z.object({
    weight: z.string().nullish(),
    massUnit: z.string().nullish(),
    length: z.string().nullish(),
    width: z.string().nullish(),
    height: z.string().nullish(),
    distanceUnit: z.string().nullish(),
  }),
);

export const ShippoShipmentGetResponseSchema =
  pinned<ShippoShipmentGetResponse>()(
    z.object({
      objectId: z.string().nullish(),
      addressFrom: ShippoShipmentAddressSchema.nullish(),
      addressTo: ShippoShipmentAddressSchema.nullish(),
      parcels: z.array(ShippoShipmentParcelSchema).nullish(),
      rates: z.array(ShippoRateResponseSchema).nullish(),
    }),
  );

// ---------------------------------------------------------------------------
// Transaction (label-buy) — `shippo.transactions.{create,get}`
// ---------------------------------------------------------------------------

/**
 * Parsed Shippo Transaction envelope as the adapter consumes it inside
 * `purchaseLabel` + `getTransaction`. Carries only the fields the adapter
 * maps into our `PurchasedLabel` domain shape — extras (`labelFileType`,
 * `objectCreated`, `objectOwner`, `parcel`, `eta`, …) are stripped.
 */
export interface ShippoTransactionResponseMessage {
  text?: string | null;
  code?: string | null;
  source?: string | null;
}

export interface ShippoTransactionResponse {
  objectId?: string | null;
  status?: string | null;
  labelUrl?: string | null;
  qrCodeUrl?: string | null;
  trackingNumber?: string | null;
  messages?: ShippoTransactionResponseMessage[] | null;
}

export const ShippoTransactionResponseMessageSchema =
  pinned<ShippoTransactionResponseMessage>()(
    z.object({
      text: z.string().nullish(),
      code: z.string().nullish(),
      source: z.string().nullish(),
    }),
  );

export const ShippoTransactionResponseSchema =
  pinned<ShippoTransactionResponse>()(
    z.object({
      objectId: z.string().nullish(),
      status: z.string().nullish(),
      labelUrl: z.string().nullish(),
      qrCodeUrl: z.string().nullish(),
      trackingNumber: z.string().nullish(),
      messages: z.array(ShippoTransactionResponseMessageSchema).nullish(),
    }),
  );

// ---------------------------------------------------------------------------
// Tracking — `shippo.trackingStatus.get(trackingNumber, carrier)`
// ---------------------------------------------------------------------------

/**
 * Parsed Shippo `Track` envelope as the adapter consumes it inside
 * `getTrackingStatus`. Only the nested `trackingStatus.{status, statusDate,
 * statusDetails}` is read — full tracking history + addresses are dropped.
 *
 * `statusDate` is `Date | null | undefined` because the SDK normalises
 * Shippo's ISO-8601 wire string into a `Date` before we see it.
 */
export interface ShippoTrackingStatusSummary {
  status?: string | null;
  statusDate?: Date | null;
  statusDetails?: string | null;
}

export interface ShippoTrackResponse {
  trackingStatus?: ShippoTrackingStatusSummary | null;
}

export const ShippoTrackingStatusSummarySchema =
  pinned<ShippoTrackingStatusSummary>()(
    z.object({
      status: z.string().nullish(),
      statusDate: z.date().nullish(),
      statusDetails: z.string().nullish(),
    }),
  );

export const ShippoTrackResponseSchema = pinned<ShippoTrackResponse>()(
  z.object({
    trackingStatus: ShippoTrackingStatusSummarySchema.nullish(),
  }),
);

// ---------------------------------------------------------------------------
// Refund — `shippo.refunds.create({ transaction })`
// ---------------------------------------------------------------------------

export interface ShippoRefundResponse {
  objectId?: string | null;
  status?: string | null;
}

export const ShippoRefundResponseSchema = pinned<ShippoRefundResponse>()(
  z.object({
    objectId: z.string().nullish(),
    status: z.string().nullish(),
  }),
);

// ---------------------------------------------------------------------------
// Adapter-boundary parse error
// ---------------------------------------------------------------------------

/**
 * Tagged error thrown by `lib/shippo/client.ts` when any of the schemas
 * above fail at `safeParse`. Carries the seam name (`shipments.create`,
 * `addresses.create`, …) + raw Zod issues so the adapter's caller (and
 * upstream log scrape) can attribute the failure to the right SDK boundary.
 *
 * Existing adapter callers (`lib/shippo/labels.ts::purchaseLabelForOrder`,
 * `app/api/shipping/rates/route.ts`, …) already wrap adapter calls in
 * try/catch and surface the error as a 5xx / `label_status='failed'`, so
 * this fits the existing error-propagation contract.
 */
export class ShippoResponseParseError extends Error {
  readonly seam: string;
  readonly issues: ReadonlyArray<z.ZodIssue>;
  constructor(seam: string, issues: ReadonlyArray<z.ZodIssue>) {
    const summary = issues
      .map((i) => `${i.path.join(".") || "<root>"}: ${i.message}`)
      .join("; ");
    super(`Shippo ${seam} response did not match contract: ${summary}`);
    this.name = "ShippoResponseParseError";
    this.seam = seam;
    this.issues = issues;
  }
}
